import React from 'react'
import { Box, Breadcrumbs, Link, Typography, Button, CircularProgress } from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { Link as RouterLink } from 'react-router-dom'

import { ROUTE_ACCOUNT, ROUTE_FAQS } from '~/routes/Routes'
import { BrandColor } from '~/config/theme'

interface LocationServicesProps {
  isLoading: boolean
  locationEnabled: boolean
  checkLocation: () => Promise<void>
}

const LocationServices: React.FC<LocationServicesProps> = ({ isLoading, locationEnabled, checkLocation }) => {
  const handleCheckLocation = (): void => {
    void checkLocation()
  }

  const renderLoading = (): JSX.Element => {
    return <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
      <CircularProgress />
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant='h6'>Checking Location Services</Typography>
      </Box>
    </Box>
  }

  const renderStatus = (): JSX.Element => {
    return <>
      <Typography variant='h4' sx={{ my: 2 }}>Location Services</Typography>
      <Typography variant='body1'>Please ensure your location services are enabled and use the below to refresh your current location. We will never be able to use your current location without your permission and only whilst this app is in use.</Typography>
      <Typography variant='h6' sx={{ mt: 2 }}>Status</Typography>

      <Box sx={{ borderColor: 'black', borderStyle: 'solid', borderWidth: 1, mt: 1, p: 2, width: { md: 365 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left' }}>
          <LocationOnIcon sx={{ width: 40, height: 40 }}/>
          <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant='body1'>{locationEnabled ? 'Enabled' : 'Disabled'}</Typography>
          </Box>
          {locationEnabled ? <CheckCircleIcon sx={{ ml: 'auto', color: BrandColor.GREEN }} /> : <CancelIcon sx={{ ml: 'auto' }} />}
        </Box>
      </Box>

      {!locationEnabled && <Box sx={{ mt: 2 }}>
          <Typography variant='h6' gutterBottom>Location not enabled?</Typography>
          <Typography variant='body1' gutterBottom>Check our <Link component={RouterLink} to={ROUTE_FAQS}>FAQs</Link> for more advice on enabling location services or contact us via your account page.</Typography>
        </Box>
      }

      <Box sx={{ display: 'flex', justifyContent: 'left', mt: 2 }}>
        {locationEnabled
          ? <Button variant='contained' component={RouterLink} to={ROUTE_ACCOUNT} sx={{ width: { xs: '100%', md: 400 } }}>Back to Account</Button>
          : <Button variant='contained' onClick={handleCheckLocation} sx={{ width: { xs: '100%', md: 400 } }}>Check Service Status</Button>
        }
      </Box>
    </>
  }

  return <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
    <Breadcrumbs aria-label='breadcrumb'>
      <Link component={RouterLink} to={ROUTE_ACCOUNT}>Account</Link>
      <Typography
        sx={{ display: 'flex', alignItems: 'center' }}
        color='text.primary'
      >Location Services</Typography>
    </Breadcrumbs>

    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, mt: 1 }}>
      {isLoading ? renderLoading() : renderStatus()}
    </Box>
  </Box>
}

export default LocationServices
